 import React, { useEffect, useRef } from 'react';                             
  import { compose } from 'redux';                                              
  import { connect } from 'react-redux';
  import { withRouter } from 'react-router-dom';                                
  import loadable from '@loadable/component';
  import { useDispatch } from 'react-redux';                                    
   
  import {
    sendVerificationEmail,
    hasCurrentUserErrors,
    setAutoVacationReopened,
  } from                   
  '../../ducks/user.duck';
  import { logout, authenticationInProgress } from '../../ducks/auth.duck';     
  import { manageDisableScrolling } from '../../ducks/ui.duck';                 
  import { withViewport } from '../../util/uiHelpers';                          
  import { hydrateCart } from '../../ducks/cart.duck';                          
  import { fetchPaypalMerchantSignupStatus } from '../../ducks/paypal.duck';    
  import { FormattedMessage } from '../../util/reactIntl';
  import {                                                                      
    getPayPalMerchantBannerContent,
    shouldShowPayPalMerchantBanner,                                             
  } from '../../util/paypalMerchantStatus';
  import { PAYPAL_ENABLED } from '../../util/paypalPhaseOut';
  import { Banner } from '../../components';
  import SaleAnnouncement from './SaleAnnouncement/SaleAnnouncement';                                    
                  
  const Topbar = loadable(() => import(/* webpackChunkName: "Topbar" */         
  './Topbar/Topbar'));
                                                                                
  const PAGES_TO_SKIP_CART_HYDRATION = new Set([
    'TransactionPage',
    // Add more pages here...                                                   
  ]);
  /**                                                                           
   * Topbar container component, which is connected to Redux Store.             
   * @component
   * @param {Object} props                                                      
   * @param {number} props.notificationCount number of notifications
   * @param {Function} props.onLogout logout function                           
   * @param {Function} props.onManageDisableScrolling manage disable scrolling 
  function                                                                      
   * @param {Function} props.onResendVerificationEmail resend verification email
   function                                                                     
   * @param {Object} props.sendVerificationEmailInProgress send verification 
  email in progress                                                             
   * @param {Object} props.sendVerificationEmailError send verification email 
  error                                                                         
   * @param {boolean} props.hasGenericError has generic error
   * @returns {JSX.Element}                                                     
   */             
  export const TopbarContainerComponent = props => {
    const {                                                                     
      notificationCount = 0,
      hasGenericError,                                                          
      viewport,   
      isDashboardHeader,
      isFullWidthHeader,
      currentUser,
      currentPage,                                                              
      fetchPaypalMerchantInProgress,
      fetchPaypalMerchantError,
      paypalMerchant,
      history,
      autoVacationReopened,
      onDismissAutoVacationReopened,
      ...rest
    } = props;
    const dispatch = useDispatch();
    const merchantId = currentUser?.attributes?.profile?.publicData?.merchantId
  || null;
    const requestedPaypalMerchantIdRef = useRef(null);

    useEffect(() => {
      // PayPal is being phased out: no PayPal status lookups or reconnect banner
      if (!PAYPAL_ENABLED || !merchantId) {
        requestedPaypalMerchantIdRef.current = null;
        return;
      }

      if (paypalMerchant?.merchant_id === merchantId) {
        requestedPaypalMerchantIdRef.current = merchantId;
        return;
      }

      // A previous fetch already failed — don't retry on every remount.
      if (fetchPaypalMerchantError) {
        return;
      }

      if (
        fetchPaypalMerchantInProgress ||
        requestedPaypalMerchantIdRef.current === merchantId
      ) {
        return;
      }

      // Avoid retry loops in the topbar when merchant-status lookup fails.
      requestedPaypalMerchantIdRef.current = merchantId;
      dispatch(fetchPaypalMerchantSignupStatus()).catch(() => {});
    }, [dispatch, fetchPaypalMerchantError, fetchPaypalMerchantInProgress, merchantId,
  paypalMerchant?.merchant_id]);                                                
   
    const showPaypalMerchantBanner = PAYPAL_ENABLED && shouldShowPayPalMerchantBanner({           
      currentUser,
      merchantStatus: paypalMerchant,                                           
    });           
    const paypalBannerContent = getPayPalMerchantBannerContent({
      currentUser,                                                              
      merchantStatus: paypalMerchant,
    });                                                                         
                  
    useEffect(() => {
      if (PAGES_TO_SKIP_CART_HYDRATION.has(currentPage)) {
        return;                                                                 
      }
      dispatch(hydrateCart());                                                  
    }, [dispatch, currentPage, currentUser?.id?.uuid]);
    return (
      <>                                                                        
        <SaleAnnouncement
          currentUser={currentUser}
          currentPage={currentPage}
          isLoggedInAs={rest.isLoggedInAs}
          onManageDisableScrolling={rest.onManageDisableScrolling}
        />
        {autoVacationReopened ? (
          <Banner
            message={<FormattedMessage id="TopbarContainer.autoVacationReopened" />}
            onDismiss={onDismissAutoVacationReopened}
          />
        ) : null}
        {showPaypalMerchantBanner ? (
          <Banner                                                               
            dismissible={false}
            message={
              <FormattedMessage
                id={paypalBannerContent.messageId}
                defaultMessage={paypalBannerContent.defaultMessage}             
              />
            }                                                                   
            ctaLabel="Reconnect PayPal"
            ctaOnClick={() => history.push('/account/paypal-merchant')}
          />                                                                    
        ) : null}
        <Topbar
          notificationCount={notificationCount}
          showGenericError={hasGenericError}
          viewport={viewport}
          isDashboardHeader={isDashboardHeader}
          currentUser={currentUser}
          currentPage={currentPage}
          history={history}
          isFullWidthHeader={isFullWidthHeader}
          {...rest}
        />
      </>                                                                       
    );            
  };

  const mapStateToProps = state => {
    // Topbar needs isAuthenticated and isLoggedInAs
    const { isAuthenticated, isLoggedInAs, logoutError, authScopes } =          
  state.auth;
    // Topbar needs user info.                                                  
    const {       
      currentUser,
      currentUserHasListings,
      currentUserHasOrders,                                                     
      currentUserSaleNotificationCount = 0,
      currentUserOrderNotificationCount = 0,                                    
      sendVerificationEmailInProgress,
      sendVerificationEmailError,
      autoVacationReopened,
    } = state.user;
    const { fetchPaypalMerchantInProgress, fetchPaypalMerchantError, paypalMerchant } = state.paypal;
    const hasGenericError = !!(logoutError || hasCurrentUserErrors(state));     
    return {
      authInProgress: authenticationInProgress(state),                          
      currentUser,
      currentUserHasListings,                                                   
      currentUserHasOrders,
      notificationCount: currentUserSaleNotificationCount +                     
  currentUserOrderNotificationCount,
      isAuthenticated,
      isLoggedInAs,                                                             
      authScopes,
      sendVerificationEmailInProgress,                                          
      sendVerificationEmailError,
      hasGenericError,
      fetchPaypalMerchantInProgress,
      fetchPaypalMerchantError,
      paypalMerchant,
      autoVacationReopened,
    };
  };                                                                            
                  
  const mapDispatchToProps = dispatch => ({
    onLogout: historyPush => dispatch(logout(historyPush)),
    onManageDisableScrolling: (componentId, disableScrolling) =>                
      dispatch(manageDisableScrolling(componentId, disableScrolling)),
    onResendVerificationEmail: () => dispatch(sendVerificationEmail()),
    onDismissAutoVacationReopened: () => dispatch(setAutoVacationReopened(false)),         
  });             
                                                                                
  // Note: it is important that the withRouter HOC is **outside** the
  // connect HOC, otherwise React Router won't rerender any Route
  // components since connect implements a shouldComponentUpdate                
  // lifecycle hook.
  //                                                                            
  // See: https://github.com/ReactTraining/react-router/issues/4671
  const TopbarContainer = compose(                                              
    withRouter,
    connect(                                                                    
      mapStateToProps,
      mapDispatchToProps
    ),
    withViewport
  )(TopbarContainerComponent);                                                  
   
  export default TopbarContainer;
